import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AppTrainingsRadarChart from '../../../components/charts/app-trainings-radar-chart/AppTrainingsRadarChart';
import AppTrainingsSkillsRadarChart from '../../../components/charts/app-trainings-skills-radar-chart/AppTrainingsSkillsRadarChart';
import AppFileContainer from '../../../components/designe/app-file-container/AppFileContainer';
import AppButton from '../../../components/designe/buttons/app-button/AppButton';
import AppFormDateTimePicker from '../../../components/designe/forms/app-form-date-time-picker';
import AppFormTextField from '../../../components/designe/forms/app-form-text-field';
import AppInfoTooltipButton from '../../../components/designe/tooltips/AppInfoTooltipButton';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import AppDialog from '../../../components/reusable/app-dialog/AppDialog';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import ViewField from '../../../components/reusable/view-field/ViewField';
import { useFinishingTrainingExercises } from '../../../hooks/useFinishingTrainingExercises';
import { useGetTrainingActivityTypes } from '../../../redux/api/trainingsAPI';
import { TrainingStatus } from '../../../shared/constants';
import {
  calculateTrainingTime,
  getTimeValueFromOptionValue,
} from '../../../utils/datetime/date-time-utils';
import {
  formatTrainingCalories,
  getTrainingActivityTypeName,
  shouldShowTrainingCalories,
} from '../../../utils/training';
import { useStyles } from './FinishingTraining.styles';
import CreateProgramFromTraining from './create-program-from-training/CreateProgramFromTraining';
import { useFinishingTrainingPage } from './useFinishingTrainingPage';

// interface IFinishingTrainingProps {}

export function FinishingTraining() {
  //props: IFinishingTrainingProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const {
    training,
    finishConfirm,
    setFinishConfirm,
    showCreateProgramModal,
    setShowCreateProgramModal,
    formik,
    isLoading,
    isTrainingLoading,
    isTrainingFetching,
    isTrainingError,
    trainingError,
    refetchTraining,
    loadingAction,
    trainingTime,
    onFilesToUploadChanged,
    onFinishTraining,
    onUpdateProgram,
    onCreateProgram,
  } = useFinishingTrainingPage();
  const { data: activityTypes = [] } = useGetTrainingActivityTypes();
  const { exercises } = useFinishingTrainingExercises(training?.id);
  const activityTypeName = getTrainingActivityTypeName(training, activityTypes);
  const handleRetry = useCallback(() => {
    void refetchTraining();
  }, [refetchTraining]);
  useRegisterPageContainerError({
    errors: isTrainingError && trainingError ? [trainingError] : [],
    isLoading: !training && (isTrainingLoading || isTrainingFetching),
    hasData: Boolean(training),
    onRetry: handleRetry,
  });

  const isTrainingFinished = training?.status === TrainingStatus.finished;
  const isCreateProgramLoading = isLoading && loadingAction === 'create';
  const isUpdateProgramLoading = isLoading && loadingAction === 'update';
  const isFinishOnlyLoading = isLoading && loadingAction === 'normal';

  return (
    <PageContainer title={t('trainings.finishingTraining')} backButton>
      <Box display="flex" flexDirection="column" gap={1}>
        {/*<Typography variant="text-md-sb" marginBottom={1}>*/}
        {/*  {t('trainings.addFiles')}*/}
        {/*</Typography>*/}

        <Box data-onboarding="finishingTraining.photos">
          <AppFileContainer
            files={training?.files}
            editMode={true}
            onFilesToUploadChanged={onFilesToUploadChanged}
          />
        </Box>

        <Box
          className={classes.dateFieldsRow}
          data-onboarding="finishingTraining.dates">
          <Box className={classes.dateFieldItem}>
            <AppFormDateTimePicker
              label={t('trainings.startDate')}
              name="startDate"
              formik={formik}
              maxDateTime={formik.values.finishDate || null}
            />
          </Box>
          <Box className={classes.dateFieldItem}>
            <Box className={classes.finishDateFieldWrapper}>
              <Box className={classes.finishDateInput}>
                <AppFormDateTimePicker
                  label={t('trainings.finishDate')}
                  name="finishDate"
                  formik={formik}
                  minDateTime={formik.values.startDate || null}
                />
              </Box>
              <Box marginTop={1.5}>
                <AppInfoTooltipButton
                  className={classes.finishDateTooltipButton}
                  text={t('trainings.finishingDatesHint')}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box data-onboarding="finishingTraining.notes">
          <AppFormTextField
            label={t('trainings.comment')}
            fullWidth
            multiline
            minRows={3}
            maxRows={15}
            name="comment"
            formik={formik}
          />
        </Box>

        {!!training?.totalLoad && (
          <ViewField
            value={training?.totalLoad}
            label={t('trainings.totalLoad')}
          />
        )}

        {activityTypeName && (
          <ViewField
            value={activityTypeName}
            label={t('trainings.activityType')}
          />
        )}

        {shouldShowTrainingCalories(training?.totalCalories) && (
          <ViewField
            value={formatTrainingCalories(training.totalCalories)}
            label={t('trainings.totalCalories')}
            tooltip={
              <AppInfoTooltipButton text={t('trainings.totalCaloriesHint')} />
            }
          />
        )}

        {trainingTime > 0 && (
          <ViewField
            value={getTimeValueFromOptionValue(trainingTime, t)}
            label={t('statistics.totalTime')}
          />
        )}

        <Box display="flex" flexDirection="column" gap={1} width="100%">
          <Typography
            variant="h3"
            className={classes.chartTitle}
            data-onboarding="finishingTraining.loadDistribution">
            {t('statistics.loadDistribution')}
          </Typography>
          <Box className={classes.radarChartContainer}>
            <AppTrainingsRadarChart
              trainings={training ? [training] : null}
              exercises={exercises}
            />
          </Box>

          <Typography variant="h3" className={classes.chartTitle}>
            {t('statistics.skillsDistribution')}
          </Typography>
          <Box className={classes.radarChartContainer}>
            <AppTrainingsSkillsRadarChart
              trainings={training ? [training] : null}
              exercises={exercises}
            />
          </Box>
        </Box>
      </Box>

      <Box className={classes.buttonActionWrapper}>
        {training?.permissions?.canCreateProgram && (
          <AppButton
            data-onboarding="finishingTraining.programActions"
            disabled={
              isTrainingFinished || (isLoading && !isCreateProgramLoading)
            }
            loading={isCreateProgramLoading}
            variant="outlined"
            onClick={() => setShowCreateProgramModal(true)}>
            {t('trainings.finishAndCreateProgram')}
          </AppButton>
        )}

        {training?.programItemId && training?.permissions?.canUpdateProgram && (
          <AppButton
            data-onboarding="finishingTraining.programActions"
            disabled={
              isTrainingFinished || (isLoading && !isUpdateProgramLoading)
            }
            loading={isUpdateProgramLoading}
            variant="outlined"
            onClick={() => setFinishConfirm('update')}>
            {t('trainings.finishAndUpdateProgram')}
          </AppButton>
        )}

        <AppButton
          data-onboarding="finishingTraining.finishTraining"
          disabled={isTrainingFinished || (isLoading && !isFinishOnlyLoading)}
          loading={isFinishOnlyLoading}
          onClick={() => setFinishConfirm('normal')}>
          {t('trainings.finishTraining')}
        </AppButton>
      </Box>

      <ConfirmDialog
        open={!!finishConfirm}
        onClose={() => setFinishConfirm(null)}
        title={
          finishConfirm === 'update'
            ? t('trainings.updateProgramConfirmQuestion')
            : t('trainings.finishConfirmQuestion')
        }
        isLoading={isLoading}
        onSubmit={() =>
          finishConfirm === 'update' ? onUpdateProgram() : onFinishTraining()
        }>
        {finishConfirm === 'update'
          ? t('trainings.updateProgramConfirmMessage')
          : t('trainings.finishConfirmMessage')}
      </ConfirmDialog>

      <AppDialog
        open={showCreateProgramModal}
        onClose={() => setShowCreateProgramModal(false)}
        title={t('trainings.createProgramFromTraining')}>
        <CreateProgramFromTraining
          training={training}
          onCreateProgram={onCreateProgram}
          onCancel={() => setShowCreateProgramModal(false)}
        />
      </AppDialog>
    </PageContainer>
  );
}
